/**
 * Reminder Detail Screen — view and manage a single reminder.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Alert,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  Share,
  Switch,
  Linking,
} from 'react-native';
import Animated, { FadeInDown } from 'react-native-reanimated';
import * as Haptics from 'expo-haptics';
import * as Clipboard from 'expo-clipboard';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { Ionicons } from '@expo/vector-icons';
import { DateTime } from 'luxon';
import { useRemindersStore } from '../../src/stores/reminderStore';
import { useAuthStore } from '../../src/stores/authStore';
import { useSettingsStore } from '../../src/stores/settingsStore';
import * as api from '../../src/api/client';
import { useTheme, type ThemeColors } from '../../src/utils/theme';
import { FONT_SIZE, SPACING, RADIUS, SHADOW } from '../../src/utils/design-tokens';
import type { Reminder } from '../../src/types/api';
import { getReminderStatus } from '../../src/types/api';
import { formatDateTime, formatDateTimeWithWeekday, formatRecurrenceRule } from '../../src/utils/localization';
import DateTimePicker from '../../src/components/common/DateTimePicker';
import RecurrenceEditor from '../../src/components/common/RecurrenceEditor';
import TagPicker from '../../src/components/common/TagPicker';
import ConfirmModal from '../../src/components/common/ConfirmModal';
import { updateSettings } from '../../src/api/client';
import { useBackHandler, leaveGuardedScreen } from '../../src/navigation/useBackHandler';
import { normalizeTagColorKeys } from '../../src/utils/tagColors';

const getStatusLabels = (colors: ThemeColors): Record<string, { icon: React.ComponentProps<typeof Ionicons>['name']; color: string; labelKey: string }> => ({
  active: { icon: 'checkmark-circle-outline', color: colors.success, labelKey: 'reminder.active' },
  snoozed: { icon: 'moon-outline', color: '#f59e0b', labelKey: 'reminder.snoozed' },
  paused: { icon: 'pause-circle-outline', color: colors.textSecondary, labelKey: 'reminder.paused' },
  delivered: { icon: 'mail-open-outline', color: colors.primary, labelKey: 'reminder.sent' },
  done: { icon: 'checkmark-done-outline', color: colors.success, labelKey: 'reminder.done' },
});

const DURATION_OPTIONS: Array<{ label: string; minutes: number | null }> = [
  { label: 'None', minutes: null },
  { label: '15m', minutes: 15 },
  { label: '30m', minutes: 30 },
  { label: '1h', minutes: 60 },
  { label: '2h', minutes: 120 },
  { label: '4h', minutes: 240 },
];

const parseTagList = (tags: string) => tags.split(',').map((tag) => tag.trim()).filter(Boolean);

const formatDurationLabel = (minutes: number | null, emptyLabel: string) => {
  if (!minutes) return emptyLabel;
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  }
  return `${minutes}m`;
};

const sentenceCase = (value: string) => value ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : value;

const withReminderTime = <T extends { hour?: number; minute?: number }>(recurJson: T | null, runAtUtc?: string | null): T | null => {
  if (!recurJson || !runAtUtc) return recurJson;
  const dateTime = DateTime.fromISO(runAtUtc);
  if (!dateTime.isValid) return recurJson;
  return { ...recurJson, hour: dateTime.hour, minute: dateTime.minute } as T;
};

const recurrencePatternOnly = (value: string | null): string | null => {
  if (!value) return value;
  try {
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== 'object') return value;
    const pattern = { ...parsed };
    delete pattern.hour;
    delete pattern.minute;
    return JSON.stringify(pattern);
  } catch {
    return value;
  }
};

export default function ReminderDetailScreen() {
  const { seq } = useLocalSearchParams<{ seq: string }>();
  const router = useRouter();
  const navigation = useNavigation();
  const { t, i18n } = useTranslation();
  const { snooze, pause, resume, skip, markDone, undoDone, deleteReminder,
    syncReminderToGoogle, unsyncReminderFromGoogle } = useRemindersStore();
  const googleConnected = useSettingsStore((s) => s.googleConnected);
  const [googleBusy, setGoogleBusy] = useState(false);

  // Read from local store first for instant display
  const localReminder = useRemindersStore(
    (s) => s.reminders.find((r) => r.user_seq === Number(seq)) ?? null
  );

  const [reminder, setReminder] = useState<Reminder | null>(localReminder);
  const [isLoading, setIsLoading] = useState(!localReminder);
  const [editBody, setEditBody] = useState('');
  const [editTags, setEditTags] = useState('');
  const [editRunAt, setEditRunAt] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showSnoozePicker, setShowSnoozePicker] = useState(false);
  const [showSnoozeOptions, setShowSnoozeOptions] = useState(false);
  const [showRecurrenceEditor, setShowRecurrenceEditor] = useState(false);
  const [editRecurJson, setEditRecurJson] = useState<string | null>(null);
  const [editIsRecurring, setEditIsRecurring] = useState(false);
  const [editDuration, setEditDuration] = useState<number | null>(null);
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showUnsavedConfirm, setShowUnsavedConfirm] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [isActionBusy, setIsActionBusy] = useState(false);
  const actionInFlightRef = React.useRef(false);
  const allowNextNavigationRef = React.useRef(false);
  const userTagColors = useAuthStore((s) => s.user?.tagColors ?? null);
  const updateUser = useAuthStore((s) => s.updateUser);
  const { colors } = useTheme();
  const s = useMemo(() => createStyles(colors), [colors]);
  const statusLabels = useMemo(() => getStatusLabels(colors), [colors]);

  // Derive all unique tags across reminders for suggestions
  const allReminders = useRemindersStore((s) => s.reminders);
  const suggestedTags = useMemo(() => {
    const set = new Set<string>();
    for (const r of allReminders) {
      if (r.tags) r.tags.split(',').forEach(t => { const k = t.trim().toLowerCase(); if (k) set.add(k); });
    }
    return Array.from(set).sort();
  }, [allReminders]);

  const applyReminderToEditState = useCallback((nextReminder: Reminder) => {
    setEditBody(nextReminder.body);
    setEditTags(nextReminder.tags || '');
    setEditRunAt(nextReminder.run_at_utc || '');
    setEditRecurJson(nextReminder.recur_json);
    setEditIsRecurring(!!nextReminder.is_recurring);
    setEditDuration(nextReminder.duration_minutes ?? null);
  }, []);

  // Initialize edit fields once when reminder first loads (not on subsequent store updates)
  const didInitEditRef = React.useRef(false);
  useEffect(() => {
    if (reminder && !didInitEditRef.current) {
      didInitEditRef.current = true;
      applyReminderToEditState(reminder);
    }
  }, [applyReminderToEditState, reminder]);

  // Track unsaved changes
  const hasChanges = useMemo(() => {
    if (!reminder) return false;
    return (
      editBody.trim() !== reminder.body ||
      editTags.trim() !== (reminder.tags || '') ||
      (editRunAt || '') !== (reminder.run_at_utc || '') ||
      editIsRecurring !== !!reminder.is_recurring ||
      editRecurJson !== reminder.recur_json ||
      editDuration !== (reminder.duration_minutes ?? null)
    );
  }, [editBody, editTags, editRunAt, editIsRecurring, editRecurJson, editDuration, reminder]);

  // Actually leave the screen. On web this is guard-aware (skips the history
  // back-guard entry so we land on the previous page, not stay stuck here).
  const leaveScreen = useCallback(() => {
    allowNextNavigationRef.current = true;
    leaveGuardedScreen(() => router.back());
  }, [router]);

  // Returns TRUE if the back was consumed here (an overlay was closed, or the
  // unsaved-changes prompt was shown) — i.e. we should stay. Returns FALSE if
  // there's nothing to consume and the screen should leave. It never navigates
  // itself, so it's safe to call from both the in-app button and the
  // browser/hardware back handler.
  const handleBack = useCallback(() => {
    if (showDatePicker) { setShowDatePicker(false); return true; }
    if (showRecurrenceEditor) { setShowRecurrenceEditor(false); return true; }
    if (showTagPicker) { setShowTagPicker(false); return true; }
    if (showDeleteConfirm) { setShowDeleteConfirm(false); return true; }
    if (showSnoozePicker) { setShowSnoozePicker(false); return true; }
    if (showSnoozeOptions) { setShowSnoozeOptions(false); return true; }
    if (showUnsavedConfirm) { setShowUnsavedConfirm(false); return true; }
    if (hasChanges) { setShowUnsavedConfirm(true); return true; }
    return false; // nothing to consume → caller leaves the screen
  }, [hasChanges, showDatePicker, showDeleteConfirm, showRecurrenceEditor, showSnoozeOptions, showSnoozePicker, showTagPicker, showUnsavedConfirm]);

  // In-app "Back" button: consume overlays first, otherwise leave.
  const onBackPress = useCallback(() => {
    if (!handleBack()) leaveScreen();
  }, [handleBack, leaveScreen]);

  useBackHandler({
    id: `reminder-detail-${seq}`,
    label: 'Reminder detail',
    priority: 900,
    enabled: true,
    // Return false when nothing was consumed so the browser/hardware back is
    // allowed to actually leave the screen (web guard continues; native pops).
    onBack: () => handleBack(),
  });

  // Keep in sync with store changes (optimistic updates flow through here)
  useEffect(() => {
    if (localReminder) {
      setReminder(localReminder);
      setIsLoading(false);
    }
  }, [localReminder]);

  // If not in local store, fetch from server
  useEffect(() => {
    if (!localReminder) {
      loadDetail();
    }
  }, [seq]);

  const loadDetail = async () => {
    try {
      const data = await api.getReminder(Number(seq));
      setReminder(data.reminder);
    } catch {
      Alert.alert(t('common.error'), t('common.notFound'));
      leaveScreen();
    } finally {
      setIsLoading(false);
    }
  };

  const insets = useSafeAreaInsets();

  if (isLoading) {
    return (
      <View style={[s.container, { paddingTop: insets.top }]}>
        <View style={s.loading}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      </View>
    );
  }

  if (!reminder) return null;

  const derivedStatus = getReminderStatus(reminder);
  const status = statusLabels[derivedStatus] || statusLabels.active;
  const isRecurring = !!reminder.is_recurring;
  const canSkipOccurrence = editIsRecurring && !!editRecurJson;
  const canShowSkipOccurrence = derivedStatus === 'active' && canSkipOccurrence;

  const tagList = useMemo(() => parseTagList(editTags), [editTags]);
  const runAtLabel = editRunAt
    ? formatDateTimeWithWeekday(editRunAt, i18n.language)
    : t('reminder.none');
  const recurrenceLabel = editIsRecurring
    ? sentenceCase(formatRecurrenceRule(recurrencePatternOnly(editRecurJson), i18n.language, reminder.until_utc) || t('reminder.recurring'))
    : t('reminder.oneTime', 'One-time');
  const durationLabel = formatDurationLabel(editDuration, t('reminder.noDuration', 'None'));
  const tagSummary = tagList.length > 0
    ? `${tagList.length} tag${tagList.length === 1 ? '' : 's'}`
    : t('reminder.noTags', 'No tags');

  const resetEdits = useCallback(() => {
    if (!reminder) return;
    applyReminderToEditState(reminder);
    setShowSnoozeOptions(false);
  }, [applyReminderToEditState, reminder]);

  const persistEdits = useCallback(async () => {
    if (!reminder) return false;

    try {
      const updates: { body?: string; tags?: string; runAtUtc?: string; recurJson?: object | null; isRecurring?: boolean; durationMinutes?: number | null } = {};
      const bodyChanged = editBody.trim() !== reminder.body;
      const tagsChanged = editTags.trim() !== (reminder.tags || '');

      if (bodyChanged || tagsChanged) {
        updates.body = editBody.trim();
        updates.tags = editTags.trim();
      }
      if (editRunAt && editRunAt !== reminder.run_at_utc) updates.runAtUtc = editRunAt;
      if (editIsRecurring !== !!reminder.is_recurring || editRecurJson !== reminder.recur_json) {
        updates.isRecurring = editIsRecurring;
        updates.recurJson = editRecurJson ? JSON.parse(editRecurJson) : null;
      }
      if (editDuration !== (reminder.duration_minutes ?? null)) updates.durationMinutes = editDuration;

      if (Object.keys(updates).length > 0) {
        const { reminder: updated } = await api.updateReminder(Number(seq), updates);
        if (updated) setReminder(updated);
        await useRemindersStore.getState().syncFromServer(true);
      }

      return true;
    } catch {
      Alert.alert(t('common.error'), t('common.tryAgain'));
      return false;
    }
  }, [editBody, editDuration, editIsRecurring, editRecurJson, editRunAt, editTags, reminder, seq, t]);

  const [showGoogleShiftConfirm, setShowGoogleShiftConfirm] = useState(false);
  const saveEdits = useCallback(async (): Promise<boolean> => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    // If the TIME changed on a synced reminder, tell the user it'll also
    // shift on Google Calendar (for recurring, that moves all occurrences)
    // and get a clear yes before saving. They can cancel and keep both as-is.
    const timeChanged = !!editRunAt && editRunAt !== reminder.run_at_utc;
    const isSynced = googleConnected && !!reminder.google_event_id;
    if (timeChanged && isSynced && !showGoogleShiftConfirm) {
      setShowGoogleShiftConfirm(true);
      return false; // defer save until the user confirms in the modal
    }
    return persistEdits();
  }, [persistEdits, editRunAt, reminder.run_at_utc, reminder.google_event_id, googleConnected, showGoogleShiftConfirm]);

  const discardAndGoBack = useCallback(() => {
    setShowUnsavedConfirm(false);
    resetEdits();
    leaveScreen();
  }, [resetEdits, leaveScreen]);

  const saveAndGoBack = useCallback(async () => {
    setShowUnsavedConfirm(false);
    const saved = await saveEdits();
    if (saved) {
      leaveScreen();
    }
  }, [leaveScreen, saveEdits]);

  useEffect(() => {
    const unsubscribe = navigation.addListener('beforeRemove', (event) => {
      if (allowNextNavigationRef.current) {
        allowNextNavigationRef.current = false;
        return;
      }
      if (!hasChanges || showUnsavedConfirm) return;

      event.preventDefault();
      setShowUnsavedConfirm(true);
    });

    return unsubscribe;
  }, [hasChanges, navigation, showUnsavedConfirm]);

  const handleShare = useCallback(async () => {
    const text = [
      editBody.trim() || reminder.body,
      (editRunAt || reminder.run_at_utc) ? formatDateTime(editRunAt || reminder.run_at_utc!, i18n.language) : '',
    ].filter(Boolean).join(' — ');

    try {
      if (Platform.OS === 'web') {
        await Clipboard.setStringAsync(text);
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 1800);
        return;
      }
      await Share.share({ message: text });
    } catch {
      Alert.alert(t('common.error'), t('common.tryAgain'));
    }
  }, [editBody, editRunAt, i18n.language, reminder.body, reminder.run_at_utc, t]);

  const handleAction = async (action: () => Promise<void>) => {
    if (actionInFlightRef.current) return;
    actionInFlightRef.current = true;
    setIsActionBusy(true);
    try {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
      if (hasChanges) {
        const saved = await persistEdits();
        if (!saved) return;
      }
      await action();
      const latestReminder = useRemindersStore.getState().reminders.find((item) => item.user_seq === Number(seq));
      if (latestReminder) {
        setReminder(latestReminder);
        applyReminderToEditState(latestReminder);
      }
    } catch {
      Alert.alert(t('common.error'), t('common.tryAgain'));
    } finally {
      actionInFlightRef.current = false;
      setIsActionBusy(false);
    }
  };

  const handleDelete = () => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    setShowDeleteConfirm(true);
  };

  const doDeleteAction = async (googleAction?: 'delete' | 'keep') => {
    setShowDeleteConfirm(false);
    Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
    await deleteReminder(Number(seq), googleAction ? { googleAction } : undefined);
    leaveScreen();
  };

  return (
    <KeyboardAvoidingView
      style={[s.container, { paddingTop: insets.top }]}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      {/* Header */}
      <View style={s.header}>
        <TouchableOpacity
          onPress={() => {
            Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
            onBackPress();
          }}
          style={s.backButton}
        >
          <Ionicons name="arrow-back" size={18} color={colors.primary} />
          <Text style={s.backText}>{t('common.back')}</Text>
        </TouchableOpacity>
        <View style={s.headerCenter}>
          <Text style={s.headerEyebrow}>{t('reminder.editReminder', 'Edit reminder')}</Text>
          <Text style={s.headerTitle}>#{reminder.user_seq}</Text>
        </View>
        {hasChanges ? (
          <View style={s.headerDirtyBadge}>
            <Ionicons name="ellipse" size={8} color={colors.warning} />
            <Text style={s.headerDirtyBadgeText}>{t('common.editing', 'Editing')}</Text>
          </View>
        ) : (
          <View style={s.headerSpacer} />
        )}
      </View>

      <ScrollView
        style={s.body}
        contentContainerStyle={[
          s.bodyContent,
          { paddingBottom: hasChanges ? 168 + insets.bottom : SPACING['4xl'] },
        ]}
        keyboardShouldPersistTaps="handled"
      >
        <Animated.View
          entering={FadeInDown.springify().damping(20).delay(50)}
          style={s.summaryCard}
        >
          <View style={s.summaryTopRow}>
            <View
              style={[
                s.statusBadge,
                {
                  borderColor: `${status.color}66`,
                  backgroundColor: `${status.color}18`,
                },
              ]}
            >
              <Ionicons name={status.icon} size={14} color={status.color} />
              <Text style={[s.statusText, { color: status.color }]}>
                {t(status.labelKey).toUpperCase()}
              </Text>
            </View>
            <Text style={s.summarySeq}>#{reminder.user_seq}</Text>
          </View>
          <Text style={s.summaryLabel}>{t('reminder.nextRun')}</Text>
          <Text style={s.summaryDate}>
            {runAtLabel === t('reminder.none')
              ? t('reminder.pickDateTime', 'Pick date & time')
              : runAtLabel}
          </Text>
          <View style={s.summaryMetaRow}>
            <HeroMetaChip icon="repeat-outline" label={recurrenceLabel} />
            <HeroMetaChip icon="pricetags-outline" label={tagSummary} />
            <HeroMetaChip icon="timer-outline" label={durationLabel} />
          </View>
        </Animated.View>

        <Animated.View entering={FadeInDown.springify().damping(20).delay(100)}>
          <SectionCard
            title={t('reminder.details', 'Reminder details')}
          >
            <TextInput
              style={s.taskInput}
              value={editBody}
              onChangeText={setEditBody}
              multiline
              autoCorrect={false}
              spellCheck={false}
              placeholder={t('reminder.body')}
              placeholderTextColor={colors.textSecondary}
            />
            <View style={s.inlineActionsWrap}>
              <View
                style={[s.actionGrid, isActionBusy && s.actionGridDisabled]}
                pointerEvents={isActionBusy ? 'none' : 'auto'}
              >
                {derivedStatus === 'active' && (
                  <>
                    <ActionButton
                      icon="checkmark"
                      label={t('reminder.markDone')}
                      color={colors.success}
                      onPress={() => handleAction(() => markDone(Number(seq)))}
                    />
                    <ActionButton
                      icon="moon-outline"
                      label={t('reminder.snooze', 'Snooze')}
                      color="#f59e0b"
                      onPress={() => {
                        Haptics.selectionAsync();
                        setShowSnoozeOptions((value) => !value);
                      }}
                    />
                    <ActionButton
                      icon="pause-outline"
                      label={t('reminder.pauseReminder')}
                      color={colors.textSecondary}
                      onPress={() => handleAction(() => pause(Number(seq)))}
                    />
                  </>
                )}

                {derivedStatus === 'paused' && (
                  <ActionButton
                    icon="play-outline"
                    label={t('reminder.resumeReminder')}
                    color={colors.success}
                    onPress={() => handleAction(() => resume(Number(seq)))}
                  />
                )}

                {derivedStatus === 'snoozed' && (
                  <ActionButton
                    icon="checkmark"
                    label={t('reminder.markDone')}
                    color={colors.success}
                    onPress={() => handleAction(() => markDone(Number(seq)))}
                  />
                )}

                {derivedStatus === 'done' && (
                  <ActionButton
                    icon="arrow-undo"
                    label={t('reminder.undo')}
                    color={colors.primary}
                    onPress={async () => {
                      try {
                        const result = await undoDone(Number(seq));
                        if (result.isOverdue) {
                          Alert.alert(
                            t('reminder.rescheduleTitle'),
                            t('reminder.rescheduleMsg'),
                            [
                              { text: t('reminder.reschedule15'), onPress: () => snooze(Number(seq), 15) },
                              { text: t('reminder.reschedule60'), onPress: () => snooze(Number(seq), 60) },
                              { text: t('reminder.keepOverdue'), style: 'cancel' },
                            ],
                          );
                        }
                      } catch {
                        Alert.alert(t('common.error'), t('common.tryAgain'));
                      }
                    }}
                  />
                )}

                <ActionButton
                  icon="share-outline"
                  label={shareCopied ? t('common.copied', 'Copied') : Platform.OS === 'web' ? t('common.copy', 'Copy') : t('reminder.share', 'Share')}
                  color={colors.primary}
                  onPress={handleShare}
                />
                <ActionButton
                  icon="trash-outline"
                  label={t('common.delete')}
                  color={colors.danger}
                  onPress={handleDelete}
                />
              </View>

              {showSnoozeOptions && (
                <View style={s.snoozePanel}>
                  <Text style={s.snoozePanelTitle}>{t('reminder.snoozeUntil', 'Snooze until')}</Text>
                  <View style={s.snoozeOptions}>
                    {[
                      { label: '15 min', minutes: 15 },
                      { label: '30 min', minutes: 30 },
                      { label: '45 min', minutes: 45 },
                      { label: '1 hour', minutes: 60 },
                      { label: '2 hours', minutes: 120 },
                    ].map((option) => (
                      <TouchableOpacity
                        key={option.minutes}
                        style={s.snoozeChip}
                        onPress={() => {
                          setShowSnoozeOptions(false);
                          handleAction(() => snooze(Number(seq), option.minutes));
                        }}
                      >
                        <Text style={s.snoozeChipText}>{option.label}</Text>
                      </TouchableOpacity>
                    ))}
                    <TouchableOpacity
                      style={s.snoozeChip}
                      onPress={() => {
                        Haptics.selectionAsync();
                        setShowSnoozeOptions(false);
                        setShowSnoozePicker(true);
                      }}
                    >
                      <Ionicons name="calendar-outline" size={14} color={colors.primary} />
                      <Text style={s.snoozeChipText}>{t('reminder.customTime', 'Custom')}</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              )}
            </View>
          </SectionCard>
        </Animated.View>

        <Animated.View
          entering={FadeInDown.springify().damping(20).delay(150)}
        >
          <SectionCard
            title={t('reminder.schedule', 'Schedule')}
          >
            <DetailRow
              icon="time-outline"
              label={t('reminder.nextRun')}
              value={runAtLabel}
              onPress={() => setShowDatePicker(true)}
              editable
            />
            <DetailRow
              icon="repeat-outline"
              label={t('reminder.recurrence')}
              value={recurrenceLabel}
              onPress={() => setShowRecurrenceEditor(true)}
              editable
              last={!canShowSkipOccurrence}
            />
            {canShowSkipOccurrence ? (
              <View style={s.scheduleSkipWrap}>
                <ActionButton
                  icon="play-skip-forward-outline"
                  label={t('reminder.skipOccurrence')}
                  color={colors.primary}
                  onPress={() => handleAction(() => skip(Number(seq)))}
                />
              </View>
            ) : null}
          </SectionCard>
        </Animated.View>

        <Animated.View entering={FadeInDown.springify().damping(20).delay(180)}>
          <SectionCard
            title={t('reminder.settings', 'Settings')}
          >
            <View style={s.preferenceBlock}>
              <View style={s.preferenceHeaderRow}>
                <View style={[s.detailIconBubble, s.detailIconBubbleEditable]}>
                  <Ionicons name="timer-outline" size={16} color={colors.primary} />
                </View>
                <View style={s.preferenceCopy}>
                  <Text style={s.detailLabel}>{t('reminder.duration', 'Duration')}</Text>
                  <Text style={s.preferencePrimaryValue}>{durationLabel}</Text>
                </View>
              </View>
              <View style={s.durationChipRow}>
                {DURATION_OPTIONS.map((option) => {
                  const selected = option.minutes === editDuration;
                  return (
                    <TouchableOpacity
                      key={option.label}
                      style={[s.durationChip, selected && s.durationChipActive]}
                      onPress={() => {
                        Haptics.selectionAsync();
                        setEditDuration(option.minutes);
                      }}
                    >
                      <Text style={[s.durationChipText, selected && s.durationChipTextActive]}>{option.label}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>

            <TouchableOpacity style={[s.preferenceBlock, s.preferenceBlockLast]} onPress={() => setShowTagPicker(true)}>
              <View style={s.preferenceHeaderRow}>
                <View style={[s.detailIconBubble, s.detailIconBubbleEditable]}>
                  <Ionicons name="pricetags-outline" size={16} color={colors.primary} />
                </View>
                <View style={s.preferenceCopy}>
                  <Text style={s.detailLabel}>{t('reminder.tags')}</Text>
                  <Text style={s.preferencePrimaryValue}>{tagSummary}</Text>
                </View>
                <Ionicons name="chevron-forward" size={16} color={colors.primary} />
              </View>
              <View style={s.tagChipRow}>
                {tagList.length > 0 ? (
                  tagList.map((tag) => (
                    <View key={tag} style={s.tagChip}>
                      <Text style={s.tagChipText}>{tag}</Text>
                    </View>
                  ))
                ) : (
                  <View style={s.emptyTagChip}>
                    <Text style={s.emptyTagChipText}>{t('reminder.noTags', 'No tags yet')}</Text>
                  </View>
                )}
              </View>
            </TouchableOpacity>
          </SectionCard>
        </Animated.View>

        <Animated.View entering={FadeInDown.springify().damping(20).delay(210)}>
          {/* Google Calendar — only when connected */}
          {googleConnected ? (
            <SectionCard title={t('reminder.googleSection', 'Google Calendar')}>
              {reminder.source === 'google' ? (
                <>
                  <DetailRow
                    icon="logo-google"
                    label={t('reminder.googleStatus', 'Status')}
                    value={t('reminder.googleImported', 'Imported from Google Calendar')}
                  />
                  <DetailRow
                    icon="open-outline"
                    label={t('reminder.googleView', 'View in Google Calendar')}
                    value={t('reminder.googleViewHint', 'Open this event on Google')}
                    onPress={() => {
                      const iso = reminder.run_at_utc;
                      let url = 'https://calendar.google.com/calendar/r';
                      if (iso) { const d = new Date(iso); url = `https://calendar.google.com/calendar/r/day/${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`; }
                      if (Platform.OS === 'web' && typeof window !== 'undefined') window.open(url, '_blank');
                      else Linking.openURL(url).catch(() => {});
                    }}
                    editable
                    last
                  />
                </>
              ) : reminder.google_event_id ? (
                <>
                  <DetailRow
                    icon="checkmark-circle"
                    label={t('reminder.googleStatus', 'Status')}
                    value={t('reminder.googleSynced', 'Synced to Google Calendar')}
                  />
                  <DetailRow
                    icon="open-outline"
                    label={t('reminder.googleView', 'View in Google Calendar')}
                    value={t('reminder.googleViewHint', 'Open this event on Google')}
                    onPress={() => {
                      // Open Google Calendar to this reminder's day.
                      const iso = reminder.run_at_utc;
                      let url = 'https://calendar.google.com/calendar/r';
                      if (iso) {
                        const d = new Date(iso);
                        url = `https://calendar.google.com/calendar/r/day/${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`;
                      }
                      if (Platform.OS === 'web' && typeof window !== 'undefined') window.open(url, '_blank');
                      else Linking.openURL(url).catch(() => {});
                    }}
                    editable
                  />
                  <DetailRow
                    icon="refresh-outline"
                    label={t('reminder.googleResync', 'Re-sync')}
                    value={t('reminder.googleResyncHint', 'Push the latest title & time to Google')}
                    onPress={async () => {
                      if (googleBusy) return;
                      setGoogleBusy(true);
                      await syncReminderToGoogle(Number(seq));
                      setGoogleBusy(false);
                    }}
                    editable
                  />
                  <DetailRow
                    icon="cloud-offline-outline"
                    label={t('reminder.googleUnsync', 'Stop syncing')}
                    value={t('reminder.googleUnsyncHint', 'Remove from Google, keep in FishMemory')}
                    onPress={async () => {
                      if (googleBusy) return;
                      setGoogleBusy(true);
                      await unsyncReminderFromGoogle(Number(seq));
                      setGoogleBusy(false);
                    }}
                    last
                  />
                </>
              ) : (
                <DetailRow
                  icon="cloud-upload-outline"
                  label={t('reminder.googleSyncOne', 'Add to Google Calendar')}
                  value={t('reminder.googleSyncOneHint', 'Sync just this reminder')}
                  onPress={async () => {
                    if (googleBusy) return;
                    setGoogleBusy(true);
                    await syncReminderToGoogle(Number(seq));
                    setGoogleBusy(false);
                  }}
                  editable
                  last
                />
              )}
            </SectionCard>
          ) : null}

          <SectionCard
            title={t('reminder.activity', 'Activity')}
          >
            <DetailRow
              icon="calendar-outline"
              label={t('reminder.created')}
              value={formatDateTime(reminder.created_at_utc, i18n.language)}
            />
            <DetailRow
              icon="notifications-outline"
              label={t('reminder.fireCount')}
              value={`${reminder.fire_count || 0}`}
              last
            />
          </SectionCard>
        </Animated.View>

      </ScrollView>

      {hasChanges ? (
        <View style={[s.saveBar, { bottom: Math.max(insets.bottom, SPACING.md) }]}>
          <Text style={s.saveBarTitle}>{t('reminder.unsaved', 'Unsaved changes')}</Text>
          <View style={s.saveBarButtons}>
            <TouchableOpacity style={s.discardButton} onPress={resetEdits}>
              <Text style={s.discardButtonText}>{t('common.discard', 'Discard')}</Text>
            </TouchableOpacity>
            <TouchableOpacity style={s.saveButton} onPress={saveEdits}>
              <Text style={s.saveButtonText}>{t('common.save')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      ) : null}

      {/* Date/Time Picker for edit mode */}
      <DateTimePicker
        visible={showDatePicker}
        value={editRunAt || reminder.run_at_utc || new Date().toISOString()}
        title={t('reminder.pickDateTime', 'Pick date & time')}
        onConfirm={(iso) => {
          setEditRunAt(iso);
          setShowDatePicker(false);
        }}
        onCancel={() => setShowDatePicker(false)}
      />

      <ConfirmModal
        visible={showUnsavedConfirm}
        title={t('reminder.unsavedTitle', 'Save changes?')}
        message={t('reminder.unsavedMessage', 'You have unsaved edits on this reminder.')}
        actions={[
          { label: t('common.save'), onPress: () => { void saveAndGoBack(); }, variant: 'primary' },
          { label: t('common.discard', 'Discard'), onPress: discardAndGoBack, variant: 'danger' },
        ]}
        cancelLabel={t('common.cancel')}
        onCancel={() => setShowUnsavedConfirm(false)}
      />

      {/* Recurrence Editor */}
      <RecurrenceEditor
        visible={showRecurrenceEditor}
        value={editRecurJson}
        isRecurring={editIsRecurring}
        onConfirm={(recurJson, isRec) => {
          const recurWithTime = withReminderTime(recurJson, editRunAt || reminder.run_at_utc);
          setEditRecurJson(recurWithTime ? JSON.stringify(recurWithTime) : null);
          setEditIsRecurring(isRec);
          setShowRecurrenceEditor(false);
        }}
        onCancel={() => setShowRecurrenceEditor(false)}
      />

      {/* Tag Picker */}
      <TagPicker
        visible={showTagPicker}
        tags={editTags ? editTags.split(',').map(t => t.trim()).filter(Boolean) : []}
        tagColors={userTagColors}
        suggestedTags={suggestedTags}
        onDone={async (tags, colorUpdates) => {
          setEditTags(tags.join(', '));
          setShowTagPicker(false);

          // Persist any color overrides to user settings
          if (colorUpdates && Object.keys(colorUpdates).length > 0) {
            const previousTagColors = userTagColors ? normalizeTagColorKeys(userTagColors) : null;
            const merged = { ...(previousTagColors || {}), ...colorUpdates };

            try {
              const { user: serverUser } = await updateSettings({ tagColors: merged });
              if (serverUser) {
                updateUser(serverUser);
              } else {
                updateUser({ tagColors: previousTagColors });
              }
            } catch (e) {
              console.warn('[TagColors] server save failed; reverting local override', e);
              updateUser({ tagColors: previousTagColors });
              Alert.alert(t('common.error'), t('settings.tagColorsSaveFailed', 'Tag colors could not be saved.'));
            }
          }
        }}
        onCancel={() => setShowTagPicker(false)}
      />

      {/* Delete confirmation — 3-way when the reminder is synced to Google */}
      {(() => {
        const isSynced = googleConnected && !!reminder.google_event_id;
        const baseLabel = isRecurring ? t('reminder.deleteEntireSeries') : t('common.delete');
        const actions = isSynced
          ? [
              { label: t('reminder.deleteBoth', 'Delete from app & Google'), onPress: () => { void doDeleteAction('delete'); }, variant: 'danger' as const },
              { label: t('reminder.deleteAppOnly', 'Delete from app only'), onPress: () => { void doDeleteAction('keep'); }, variant: 'danger' as const },
            ]
          : [
              { label: baseLabel, onPress: () => { void doDeleteAction(); }, variant: 'danger' as const },
            ];
        return (
          <ConfirmModal
            visible={showDeleteConfirm}
            title={isRecurring ? t('reminder.deleteRecurringTitle') : t('reminder.deleteTitle')}
            message={isSynced
              ? t('reminder.deleteConfirmSynced', 'This reminder is also on your Google Calendar. What should happen to it?')
              : t('reminder.deleteConfirm')}
            actions={actions}
            cancelLabel={t('common.cancel')}
            onCancel={() => setShowDeleteConfirm(false)}
          />
        );
      })()}

      {/* Time-shift on Google confirmation (synced reminders) */}
      <ConfirmModal
        visible={showGoogleShiftConfirm}
        title={t('reminder.googleShiftTitle', 'Update on Google Calendar?')}
        message={isRecurring
          ? t('reminder.googleShiftRecurring', "You changed the time. This will move every occurrence of this reminder on your Google Calendar too.")
          : t('reminder.googleShiftOnce', "You changed the time. This will update the event on your Google Calendar too.")}
        actions={[
          {
            label: t('reminder.googleShiftConfirm', 'Update both'),
            variant: 'primary',
            onPress: () => {
              setShowGoogleShiftConfirm(false);
              void persistEdits();
            },
          },
        ]}
        cancelLabel={t('common.cancel')}
        onCancel={() => setShowGoogleShiftConfirm(false)}
      />

      {/* Date/Time Picker for custom snooze */}
      <DateTimePicker
        visible={showSnoozePicker}
        value={reminder.run_at_utc || new Date().toISOString()}
        title={t('reminder.snoozeUntil', 'Snooze until')}
        minDate={new Date().toISOString()}
        onConfirm={(iso) => {
          setShowSnoozePicker(false);
          handleAction(async () => {
            await api.updateReminder(Number(seq), { runAtUtc: iso });
            await useRemindersStore.getState().syncFromServer();
          });
        }}
        onCancel={() => setShowSnoozePicker(false)}
      />
    </KeyboardAvoidingView>
  );
}

function SectionCard({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  const { colors } = useTheme();
  const s = useMemo(() => createStyles(colors), [colors]);

  return (
    <View style={s.sectionCard}>
      <View style={s.sectionHeader}>
        <Text style={s.sectionTitle}>{title}</Text>
        {subtitle ? <Text style={s.sectionSubtitle}>{subtitle}</Text> : null}
      </View>
      {children}
    </View>
  );
}

function HeroMetaChip({ icon, label }: { icon: React.ComponentProps<typeof Ionicons>['name']; label: string }) {
  const { colors } = useTheme();
  const s = useMemo(() => createStyles(colors), [colors]);

  return (
    <View style={s.summaryMetaChip}>
      <Ionicons name={icon} size={13} color={colors.primary} />
      <Text style={s.summaryMetaText}>{label}</Text>
    </View>
  );
}

function DetailRow({
  icon,
  label,
  value,
  onPress,
  editable,
  last,
}: {
  icon: React.ComponentProps<typeof Ionicons>['name'];
  label: string;
  value: string;
  onPress?: () => void;
  editable?: boolean;
  last?: boolean;
}) {
  const { colors } = useTheme();
  const s = useMemo(() => createStyles(colors), [colors]);
  const Wrapper = onPress ? TouchableOpacity : View;
  const wrapperProps = onPress ? { onPress, activeOpacity: 0.78 } : {};

  return (
    <Wrapper style={[s.detailRow, last && s.detailRowLast]} {...(wrapperProps as any)}>
      <View style={[s.detailIconBubble, editable && s.detailIconBubbleEditable]}>
        <Ionicons name={icon} size={16} color={editable ? colors.primary : colors.textSecondary} />
      </View>
      <View style={s.detailContent}>
        <Text style={s.detailLabel}>{label}</Text>
        <Text style={[s.detailValue, editable && s.detailValueEditable]}>{value}</Text>
      </View>
      {editable ? (
        <View style={s.detailAccessory}>
          <Ionicons name="chevron-forward" size={14} color={colors.primary} />
        </View>
      ) : null}
    </Wrapper>
  );
}

function ActionButton({
  icon,
  label,
  color,
  onPress,
  variant = 'tile',
}: {
  icon: React.ComponentProps<typeof Ionicons>['name'];
  label: string;
  color: string;
  onPress: () => void;
  variant?: 'tile' | 'secondary';
}) {
  const { colors } = useTheme();
  const s = useMemo(() => createStyles(colors), [colors]);

  return (
    <TouchableOpacity
      style={variant === 'secondary' ? s.secondaryActionButton : s.actionButton}
      onPress={() => {
        Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
        onPress();
      }}
      activeOpacity={0.7}
    >
      <View style={[s.actionIconBubble, { backgroundColor: `${color}14` }]}>
        <Ionicons name={icon} size={15} color={color} />
      </View>
      <View style={s.actionCopy}>
        <Text style={[s.actionLabel, { color }]}>{label}</Text>
      </View>
    </TouchableOpacity>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  loading: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: SPACING.base,
    paddingVertical: SPACING.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingRight: SPACING.md,
  },
  backText: {
    fontSize: FONT_SIZE.base,
    color: colors.primary,
    fontWeight: '600',
  },
  headerCenter: {
    flex: 1,
  },
  headerEyebrow: {
    fontSize: FONT_SIZE.xs,
    color: colors.textSecondary,
    fontWeight: '600',
    marginBottom: 2,
  },
  headerTitle: {
    fontSize: FONT_SIZE.md,
    fontWeight: '700',
    color: colors.text,
  },
  headerDirtyBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: SPACING.md,
    paddingVertical: 8,
    borderRadius: RADIUS.full,
    backgroundColor: colors.primaryLight,
    borderWidth: 1,
    borderColor: colors.border,
  },
  headerDirtyBadgeText: {
    fontSize: FONT_SIZE.sm,
    color: colors.primary,
    fontWeight: '700',
  },
  headerSpacer: {
    width: 72,
  },
  body: {
    flex: 1,
  },
  bodyContent: {
    padding: SPACING.lg,
  },
  summaryCard: {
    backgroundColor: colors.card,
    borderRadius: RADIUS.lg,
    padding: SPACING.base,
    marginBottom: SPACING.lg,
    borderWidth: 1,
    borderColor: colors.border,
    ...SHADOW.subtle,
  },
  summaryTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: SPACING.md,
    gap: SPACING.sm,
  },
  summarySeq: {
    fontSize: FONT_SIZE.sm,
    color: colors.textSecondary,
    fontWeight: '700',
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    paddingHorizontal: SPACING.md,
    paddingVertical: 6,
    borderRadius: RADIUS.full,
    borderWidth: 1,
    gap: 6,
  },
  statusText: {
    fontSize: FONT_SIZE.xs,
    fontWeight: '700',
    letterSpacing: 0.6,
  },
  summaryLabel: {
    fontSize: FONT_SIZE.xs,
    fontWeight: '700',
    color: colors.textSecondary,
    marginBottom: SPACING.xs,
  },
  summaryDate: {
    fontSize: FONT_SIZE.base,
    lineHeight: 22,
    color: colors.text,
    fontWeight: '700',
  },
  summaryMetaRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: SPACING.sm,
    marginTop: SPACING.md,
  },
  summaryMetaChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: SPACING.sm,
    paddingVertical: 6,
    borderRadius: RADIUS.full,
    backgroundColor: colors.primaryLight,
    borderWidth: 1,
    borderColor: colors.border,
  },
  summaryMetaText: {
    fontSize: FONT_SIZE.sm,
    color: colors.primary,
    fontWeight: '600',
  },
  sectionCard: {
    backgroundColor: colors.card,
    borderRadius: RADIUS.lg,
    padding: SPACING.base,
    borderWidth: 1,
    borderColor: colors.border,
    marginBottom: SPACING.lg,
    ...SHADOW.subtle,
  },
  sectionHeader: {
    marginBottom: SPACING.sm,
  },
  sectionTitle: {
    fontSize: FONT_SIZE.base,
    fontWeight: '700',
    color: colors.text,
  },
  sectionSubtitle: {
    marginTop: SPACING.xs,
    fontSize: FONT_SIZE.sm,
    lineHeight: 18,
    color: colors.textSecondary,
  },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    gap: SPACING.md,
  },
  detailRowLast: {
    borderBottomWidth: 0,
    paddingBottom: 0,
  },
  detailIconBubble: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.inputBackground,
  },
  detailIconBubbleEditable: {
    backgroundColor: colors.primaryLight,
  },
  detailContent: {
    flex: 1,
  },
  detailLabel: {
    fontSize: FONT_SIZE.sm,
    color: colors.textSecondary,
    marginBottom: 2,
    fontWeight: '600',
  },
  detailValue: {
    fontSize: FONT_SIZE.base,
    color: colors.text,
    fontWeight: '700',
    lineHeight: 21,
  },
  detailValueEditable: {
    color: colors.text,
  },
  detailAccessory: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: 26,
    height: 26,
    borderRadius: RADIUS.full,
    backgroundColor: colors.primaryLight,
  },
  taskInput: {
    minHeight: 84,
    borderWidth: 1,
    borderColor: colors.borderBold,
    borderRadius: RADIUS.lg,
    paddingHorizontal: SPACING.base,
    paddingVertical: SPACING.base,
    backgroundColor: colors.surfaceTinted,
    color: colors.text,
    textAlignVertical: 'top',
    fontSize: FONT_SIZE.lg,
    fontWeight: '600',
    lineHeight: 28,
  },
  preferenceBlock: {
    paddingVertical: SPACING.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  preferenceBlockLast: {
    borderBottomWidth: 0,
    paddingBottom: 0,
  },
  preferenceHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.md,
  },
  preferenceCopy: {
    flex: 1,
  },
  preferencePrimaryValue: {
    fontSize: FONT_SIZE.base,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 2,
  },
  switchWrap: {
    marginLeft: SPACING.sm,
  },
  durationChipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: SPACING.sm,
    marginTop: SPACING.md,
    paddingLeft: 50,
  },
  durationChip: {
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.sm,
    borderRadius: RADIUS.full,
    backgroundColor: colors.chipBackground,
    borderWidth: 1,
    borderColor: colors.border,
  },
  durationChipActive: {
    backgroundColor: colors.chipActiveBackground,
    borderColor: colors.chipActiveBorder,
  },
  durationChipText: {
    fontSize: FONT_SIZE.sm,
    color: colors.textSecondary,
    fontWeight: '600',
  },
  durationChipTextActive: {
    color: colors.primary,
  },
  tagChipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: SPACING.sm,
    marginTop: SPACING.md,
    paddingLeft: 50,
  },
  tagChip: {
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.sm,
    borderRadius: RADIUS.full,
    backgroundColor: colors.primaryLight,
  },
  tagChipText: {
    fontSize: FONT_SIZE.sm,
    color: colors.primary,
    fontWeight: '700',
  },
  emptyTagChip: {
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.sm,
    borderRadius: RADIUS.full,
    backgroundColor: colors.inputBackground,
  },
  emptyTagChipText: {
    fontSize: FONT_SIZE.sm,
    color: colors.textSecondary,
    fontWeight: '600',
  },
  inlineActionsWrap: {
    marginTop: SPACING.md,
    paddingTop: SPACING.md,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  scheduleSkipWrap: {
    paddingTop: SPACING.sm,
    alignItems: 'flex-start',
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.inputBackground,
    borderRadius: RADIUS.full,
    paddingVertical: 7,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderColor: colors.border,
    gap: 7,
    minHeight: 36,
    maxWidth: '100%',
  },
  secondaryActionRow: {
    flexDirection: 'row',
    gap: SPACING.md,
    marginTop: SPACING.base,
  },
  secondaryActionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.card,
    borderRadius: RADIUS.lg,
    paddingVertical: SPACING.md,
    paddingHorizontal: SPACING.base,
    borderWidth: 1,
    borderColor: colors.border,
    gap: SPACING.md,
  },
  actionGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: SPACING.sm,
  },
  actionGridDisabled: {
    opacity: 0.55,
  },
  actionIconBubble: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionCopy: {
    flexShrink: 1,
  },
  actionLabel: {
    fontSize: FONT_SIZE.sm,
    lineHeight: 17,
    fontWeight: '700',
  },
  snoozePanel: {
    marginTop: SPACING.sm,
    borderRadius: RADIUS.md,
    padding: SPACING.sm,
    backgroundColor: colors.surfaceTinted,
    borderWidth: 1,
    borderColor: colors.border,
  },
  snoozePanelTitle: {
    fontSize: FONT_SIZE.sm,
    fontWeight: '700',
    color: colors.text,
    marginBottom: SPACING.sm,
  },
  snoozeOptions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: SPACING.sm,
  },
  snoozeChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.sm,
    borderRadius: RADIUS.lg,
    backgroundColor: colors.primaryLight,
  },
  snoozeChipText: {
    fontSize: FONT_SIZE.sm,
    fontWeight: '600',
    color: colors.primary,
  },
  saveBar: {
    position: 'absolute',
    left: SPACING.lg,
    right: SPACING.lg,
    backgroundColor: colors.card,
    borderRadius: RADIUS.xl,
    padding: SPACING.md,
    borderWidth: 1,
    borderColor: colors.borderBold,
    ...SHADOW.elevated,
  },
  saveBarTitle: {
    fontSize: FONT_SIZE.base,
    fontWeight: '700',
    color: colors.text,
    marginBottom: SPACING.sm,
  },
  saveBarButtons: {
    flexDirection: 'row',
    gap: SPACING.md,
  },
  discardButton: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: SPACING.md,
    borderRadius: RADIUS.lg,
    backgroundColor: colors.primaryLight,
  },
  discardButtonText: {
    fontSize: FONT_SIZE.base,
    fontWeight: '700',
    color: colors.primary,
  },
  saveButton: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: SPACING.md,
    borderRadius: RADIUS.lg,
    backgroundColor: colors.primary,
  },
  saveButtonText: {
    fontSize: FONT_SIZE.base,
    fontWeight: '700',
    color: '#fff',
  },
});
